import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Search as SearchIcon } from "lucide-react";

export default function Search() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");
  const [cats, setCats] = useState([]);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/public/categories").then((r) => setCats(r.data)).catch(() => setCats([])); }, []);

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    setSp({ q });
    try {
      const { data } = await api.get("/public/search", { params: { q, type: type || undefined, category: category || undefined } });
      setRes(data);
    } catch { setRes({ total: 0, results: [] }); }
    setLoading(false);
  };

  const routeFor = (r) => r.kind === "crime_story" ? `/global-crime-watch/${r.slug}` : `/${r.type.replace("_", "-")}/${r.slug}`;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
      <title>Search | Be-Apuo Media Intelligence</title>
      <div className="label-caps text-ink">Search</div>
      <h1 className="mt-2 font-editorial font-black text-4xl tracking-tight text-ink">Search</h1>
      <form onSubmit={doSearch} className="mt-6 flex flex-wrap gap-2">
        <div className="flex-1 min-w-[200px] flex items-center border border-ink/20 bg-white px-3 focus-within:border-heritage">
          <SearchIcon className="h-4 w-4 text-ink/50" />
          <label htmlFor="search-input" className="sr-only">Search published stories</label>
          <input id="search-input" data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search published stories, fact-checks, crime…"
            className="flex-1 bg-transparent px-2 py-2.5 text-sm text-ink focus:outline-none" />
        </div>
        <label htmlFor="search-type-filter" className="sr-only">Filter by type</label>
        <select id="search-type-filter" data-testid="search-type-filter" value={type} onChange={(e) => setType(e.target.value)} className="border border-ink/20 bg-white px-2 text-sm text-ink focus:outline-none focus:border-heritage">
          <option value="">All</option>
          <option value="article">Articles</option>
          <option value="fact_check">Fact-checks</option>
          <option value="analysis">Analysis</option>
          <option value="crime_story">Crime</option>
        </select>
        <label htmlFor="search-section-filter" className="sr-only">Filter by section</label>
        <select id="search-section-filter" data-testid="search-section-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="border border-ink/20 bg-white px-2 text-sm text-ink focus:outline-none focus:border-heritage">
          <option value="">All sections</option>
          {cats.map((c) => <option key={c.slug || c.name} value={c.name}>{c.name}</option>)}
        </select>
        <button data-testid="search-submit" className="bg-heritage text-paper px-5 py-2.5 text-sm font-semibold hover:bg-heritage/90 transition-colors">Search</button>
      </form>

      <div className="mt-8">
        {loading && <p className="text-ink/55">Searching…</p>}
        {res && !loading && (
          <>
            <div className="label-caps text-ink/55 mb-4">{res.total} result{res.total !== 1 ? "s" : ""}</div>
            {res.results.length === 0 ? (
              <div className="border border-ink/10 bg-mist p-10 text-center text-ink/60" data-testid="search-empty">No matching published content found. Try broader keywords or clear the filters.</div>
            ) : (
              <ul className="space-y-6" data-testid="search-results">
                {res.results.map((r, i) => (
                  <li key={i} className="border-t-2 border-ink pt-4">
                    <span className="label-caps text-ink/55">{r.kind === "crime_story" ? "Crime Watch" : r.type.replace("_", "-")}</span>
                    <Link to={routeFor(r)} className="block mt-1 font-editorial font-bold text-xl text-ink hover:underline decoration-2 underline-offset-4">{r.title}</Link>
                    <p className="mt-1 text-sm text-ink/70 line-clamp-2">{r.summary}</p>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
